import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Select,
  Textarea,
  Checkbox,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Flex,
  Text,
  Stack,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";

const API = process.env.REACT_APP_API_URL;

// Countries the Checkers report wizard accepts. Everything runs as South Africa except
// BlackMamba, whose portal account is Swaziland-only.
const COUNTRIES = ["South Africa", "Swaziland"];

/**
 * Add / edit one crawler supplier.
 *
 * `existing` = null -> create mode (password required)
 *            = record -> edit mode (blank password keeps the stored one)
 * `meta` carries the retailer's vocabulary from the crawler box: runKeys, runLabels,
 * hasCountry and defaultPassword — so this form never hardcodes retailer knowledge.
 */
const SupplierModal = ({ isOpen, onClose, onSaved, meta, existing, crawlRetailer, retailerLabel }) => {
  const toast = useToast();
  const userid = localStorage.getItem("userid");
  const isEdit = Boolean(existing);

  const [supplier, setSupplier] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [runs, setRuns] = useState({});
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Live check that the EDI importer will be able to resolve this name (see below).
  const [nameCheck, setNameCheck] = useState(null);
  const [checkingName, setCheckingName] = useState(false);
  const checkTimer = useRef(null);

  const helperColor = useColorModeValue("gray.500", "gray.400");
  const runKeys = useMemo(() => meta?.runKeys || [], [meta]);

  // Reset the form each time the modal opens so a previous edit can't bleed through.
  useEffect(() => {
    if (!isOpen) return;
    setSupplier(existing?.supplier || "");
    setUsername(existing?.username || "");
    // Checkers logins share one password — prefill it for new rows to save typing.
    setPassword(existing ? "" : meta?.defaultPassword || "");
    setCountry(existing?.country || COUNTRIES[0]);
    setRuns(runKeys.reduce((acc, key) => ({ ...acc, [key]: existing?.runs?.[key] === true }), {}));
    setActive(existing ? existing.active !== false : true);
    setNotes(existing?.notes || "");
    setNameCheck(null);
  }, [isOpen, existing, meta, runKeys]);

  // The EDI importer resolves a supplier by taking the filename prefix the crawler writes
  // and looking for a `suppliers.suppliercode` that is a substring of it. If nothing
  // matches, the downloaded file bounces with "couldn't determine suppliercode" and the
  // data silently never lands — the WelnessBlend / NatureChoice failure mode. Check as
  // the name is typed so that is caught here rather than a week later.
  useEffect(() => {
    if (!isOpen) return;
    const name = supplier.trim();
    if (checkTimer.current) clearTimeout(checkTimer.current);
    if (name.length < 2) {
      setNameCheck(null);
      return;
    }
    checkTimer.current = setTimeout(async () => {
      setCheckingName(true);
      try {
        const res = await axios.get(`${API}/edi-crawler/suppliers/validate-name`, {
          params: { userid, retailer: crawlRetailer, supplier: name },
        });
        setNameCheck(res.data?.data || null);
      } catch (e) {
        setNameCheck(null);
      } finally {
        setCheckingName(false);
      }
    }, 450);
    return () => {
      if (checkTimer.current) clearTimeout(checkTimer.current);
    };
  }, [supplier, isOpen, userid, crawlRetailer]);

  const canSave = supplier.trim().length >= 2 && username.trim().length > 0 && (isEdit || password.length > 0) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        userid,
        retailer: crawlRetailer,
        supplier: supplier.trim(),
        username: username.trim(),
        runs,
        active,
        notes,
      };
      // Only send a password when one was typed — the backend treats blank as "keep".
      if (password) payload.password = password;
      if (meta?.hasCountry) payload.country = country;
      // Editing a row whose display name changed must target the original name.
      if (isEdit && existing.supplier !== supplier.trim()) payload.rename = existing.supplier;

      await axios.post(`${API}/edi-crawler/suppliers/save`, payload);
      toast({
        status: "success",
        title: isEdit ? "Supplier updated" : "Supplier added",
        description: `${supplier.trim()} saved to the ${retailerLabel} crawler registry.`,
        duration: 5000,
        isClosable: true,
      });
      onSaved();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to save supplier";
      toast({ status: "error", title: "Save failed", description: msg, duration: 7000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? `Edit ${existing.supplier}` : `Add supplier — ${retailerLabel}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Supplier name</FormLabel>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. NatureChoice" />
              <FormHelperText fontSize="xs" color={helperColor}>
                Becomes the downloaded filename prefix, which the EDI import matches against the suppliers table.
              </FormHelperText>
              {checkingName && (
                <Text fontSize="xs" color={helperColor} mt={1}>
                  Checking name against the suppliers table...
                </Text>
              )}
              {!checkingName && nameCheck && nameCheck.resolved && (
                <Text fontSize="xs" color="green.500" mt={1}>
                  ✓ Resolves to supplier code <strong>{nameCheck.matchedCode}</strong> — the EDI import will accept these files.
                </Text>
              )}
              {!checkingName && nameCheck && !nameCheck.resolved && (
                <Alert status="warning" mt={2} borderRadius="md" fontSize="xs" py={2}>
                  <AlertIcon />
                  <AlertDescription>
                    No supplier code in the database is contained in this name, so the EDI cron will reject the
                    downloaded files ("couldn't determine suppliercode"). Check the spelling against the suppliers table
                    — a single wrong letter breaks the match (e.g. <strong>WelnessBlend</strong> never matched{" "}
                    <strong>WELLNESS BLEND</strong>) — or add the supplier to that table first.
                  </AlertDescription>
                </Alert>
              )}
            </FormControl>

            <Flex gap={3} direction={{ base: "column", sm: "row" }}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Portal username</FormLabel>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. maraish10" />
              </FormControl>
              <FormControl isRequired={!isEdit}>
                <FormLabel fontSize="sm">Portal password</FormLabel>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isEdit ? "unchanged" : "required"} />
                {isEdit && (
                  <FormHelperText fontSize="xs" color={helperColor}>
                    Leave blank to keep the stored password.
                  </FormHelperText>
                )}
              </FormControl>
            </Flex>

            {meta?.hasCountry && (
              <FormControl>
                <FormLabel fontSize="sm">Country</FormLabel>
                <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="xs" color={helperColor}>
                  Selected on the report wizard's country page. Everything is South Africa except Swaziland-only accounts.
                </FormHelperText>
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Scheduled runs</FormLabel>
              <Stack spacing={1}>
                {runKeys.map((key) => (
                  <Checkbox key={key} isChecked={runs[key] === true} onChange={(e) => setRuns((prev) => ({ ...prev, [key]: e.target.checked }))}>
                    <Text fontSize="sm">{meta?.runLabels?.[key] || key}</Text>
                  </Checkbox>
                ))}
              </Stack>
              <FormHelperText fontSize="xs" color={helperColor}>
                Which scheduled crawls include this supplier. Leave all unticked to make it on-demand only. Put a supplier
                in one sales batch, not both — the batches are staggered because they share a single OTP inbox.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Flex align="center" gap={3}>
                <Switch isChecked={active} onChange={(e) => setActive(e.target.checked)} />
                <Box>
                  <Text fontSize="sm" fontWeight="medium">
                    Active
                  </Text>
                  <Text fontSize="xs" color={helperColor}>
                    Inactive suppliers keep their credentials but are skipped by every run.
                  </Text>
                </Box>
              </Flex>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Notes</FormLabel>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Optional — e.g. portal contact, OTP mailbox" />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="green" mr={3} onClick={handleSave} isDisabled={!canSave} isLoading={saving} loadingText="Saving...">
            Save
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SupplierModal;
